import { forwardRef, useId } from 'react';
import { combineClassNames } from '../../../Utils/ClassnameUtils';
import GWPRadio from './GWPRadio';

export type GWPChoiceGroupOption<T extends string | number> = {
	value: T;
	label: React.ReactNode;
	description?: React.ReactNode;
	disabled?: boolean;
};

export interface GWPChoiceGroupProps<T extends string | number>
	extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange'> {
	options: ReadonlyArray<GWPChoiceGroupOption<T>>;
	value?: T | null;
	name?: string;
	size?: 'sm' | 'md' | 'lg';
	orientation?: 'vertical' | 'horizontal';
	invalid?: boolean;
	disabled?: boolean;
	onValueChange?: (value: T, event: React.ChangeEvent<HTMLInputElement>) => void;
}

type GWPChoiceGroupComponent = <T extends string | number>(
	props: GWPChoiceGroupProps<T> & React.RefAttributes<HTMLDivElement>,
) => React.ReactElement | null;

const GWPChoiceGroupInner = forwardRef(function GWPChoiceGroupInner<T extends string | number>(
	props: GWPChoiceGroupProps<T>,
	ref: React.ForwardedRef<HTMLDivElement>,
) {
	const {
		className,
		options,
		value,
		name,
		size = 'md',
		orientation = 'vertical',
		invalid = false,
		disabled = false,
		onValueChange,
		...rest
	} = props;

	const generatedName = useId().replace(/:/g, '');
	const groupName = name ?? `gwp-choice-group-${generatedName}`;

	const rootClassName = combineClassNames(
		'gwp-choice-group',
		size,
		orientation,
		invalid ? 'invalid' : undefined,
		disabled ? 'disabled' : undefined,
		className,
	);

	return (
		<div ref={ref} className={rootClassName} role="radiogroup" aria-invalid={invalid || undefined} {...rest}>
			{options.map((option) => (
				<GWPRadio
					key={String(option.value)}
					className="choice-option"
					name={groupName}
					size={size}
					label={option.label}
					description={option.description}
					value={option.value}
					checked={option.value === value}
					disabled={disabled || option.disabled}
					invalid={invalid}
					onCheckedChange={(checked, event) => {
						if (checked) {
							onValueChange?.(option.value, event);
						}
					}}
				/>
			))}
		</div>
	);
});

GWPChoiceGroupInner.displayName = 'GWPChoiceGroup';

const GWPChoiceGroup = GWPChoiceGroupInner as GWPChoiceGroupComponent;

export default GWPChoiceGroup;
